// Player colours (kit tokens `--fk-p1..p24`) shared by the board and the HUD: each human's name, card
// header, plan rail, ghost and path draw in their colour. docs/184: a player may choose any of the 24
// (`player_colour` rows); whoever has not chosen takes the first colour no one else holds, in World
// seat order (the keeper first, then by joining), so every client agrees.
import type { UnitState } from '../../core/sim/types';

/** `--fk-p1..p24` as numbers for Phaser (docs/171 PC4, docs/184); keep in step with tokens.css. */
export const PLAYER_COLOURS = [
  0xf2a447, 0x6fa8ff, 0x6fd27a, 0xc08cff, 0xff7a8a, 0xffe066, 0x4dd9cf, 0xff99d6, 0xb5e05a, 0x8c96ff,
  0xff9f80, 0x66d1ff, 0xd9b98c, 0xe88cf0, 0x8fe0b0, 0xe8e3d4, 0xffb3b3, 0x9ec4e6, 0xc7a76b, 0xa8e8e8,
  0xc4d68c, 0xf07c3c, 0xff6b5c, 0xc9b0ff,
] as const;

/** `#rrggbb` of colour `i` (0-based), for swatches drawn outside the kit tokens. */
export const colourHex = (i: number): string => `#${PLAYER_COLOURS[i % PLAYER_COLOURS.length]!.toString(16).padStart(6, '0')}`;

/**
 * docs/184: each member's colour (0-based into `PLAYER_COLOURS`). `seated` is the members in seat
 * order; `chosen` is owner → chosen colour. A chosen colour is the player's own (the module lets one
 * player hold it); everyone else takes the lowest colour not chosen and not yet given, in seat order.
 */
export function assignColours(seated: readonly string[], chosen: ReadonlyMap<string, number>): Map<string, number> {
  const out = new Map<string, number>();
  const held = new Set<number>();
  for (const o of seated) {
    const c = chosen.get(o);
    if (c !== undefined && c >= 0 && c < PLAYER_COLOURS.length && !held.has(c)) { out.set(o, c); held.add(c); }
  }
  let next = 0;
  for (const o of seated) {
    if (out.has(o)) continue;
    while (held.has(next % PLAYER_COLOURS.length) && held.size < PLAYER_COLOURS.length) next++;
    const c = next % PLAYER_COLOURS.length;
    out.set(o, c); held.add(c); next++;
  }
  return out;
}

/**
 * owner identity hex → colour index (0-based). With the World's colours (`colours`, from
 * `assignColours`) each player keeps their lobby colour; anyone missing from it (a harness, a gallery)
 * takes the next colour in hero order (the party's deploy order).
 */
export function playerSeats(units: readonly UnitState[] | undefined, owners: Record<number, string>, colours?: ReadonlyMap<string, number>): Map<string, number> {
  const seats = new Map<string, number>();
  const used = new Set(colours?.values());
  let next = 0;
  for (const u of units ?? []) {
    if (u.heroId === undefined) continue;
    const o = owners[u.heroId];
    if (!o || seats.has(o)) continue;
    const c = colours?.get(o);
    if (c !== undefined) { seats.set(o, c); continue; }
    while (used.has(next) && used.size < PLAYER_COLOURS.length) next++;
    seats.set(o, next % PLAYER_COLOURS.length); used.add(next % PLAYER_COLOURS.length); next++;
  }
  return seats;
}

export const seatColour = (seat: number | undefined): number => PLAYER_COLOURS[(seat ?? 0) % PLAYER_COLOURS.length]!;
/** CSS custom property for a seat, e.g. `var(--fk-p2)`. */
export const seatVar = (seat: number | undefined): string => `var(--fk-p${((seat ?? 0) % PLAYER_COLOURS.length) + 1})`;
